'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { AppError, AuthenticationErrorCode } from '@repo/error';
import { t, validationError } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@repo/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/form';
import { GoogleIcon } from '@repo/ui/icons/google';
import { OidcIcon } from '@repo/ui/icons/oidc';
import { Input } from '@repo/ui/input';
import { cn } from '@repo/ui/lib/utils';
import { PasswordInput } from '@repo/ui/password-input';
import { PinInput, PinInputGroup, PinInputSlot } from '@repo/ui/pin-input';
import { browserSupportsWebAuthn, startAuthentication } from '@simplewebauthn/browser';
import { KeyRoundIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { match } from 'ts-pattern';
import { z } from 'zod';
import { authClient } from '@/providers/session';
import { trpc } from '@/providers/trpc';
import { ZCurrentPasswordSchema } from '@/types/auth';

const CommonErrorMessages: Record<string, string> = {
  get [AuthenticationErrorCode.AccountDisabled]() {
    return t('extra.thisAccountHasBeenDisabledPleaseContactSupport');
  },
};

const handleFallbackErrorMessages = (code: string) => {
  const message = CommonErrorMessages[code];

  if (!message) {
    return t('workspace.anUnknownErrorOccurred');
  }

  return message;
};

const LOGIN_REDIRECT_PATH = '/o';

export const ZSignInFormSchema = z.object({
  email: z.email().min(1),
  password: ZCurrentPasswordSchema,
  totpCode: z.string().trim().optional(),
  backupCode: z.string().trim().optional(),
});

export type TSignInFormSchema = z.infer<typeof ZSignInFormSchema>;

export type SignInFormProps = {
  className?: string;
  initialEmail?: string;
  isGoogleSSOEnabled?: boolean;
  isOIDCSSOEnabled?: boolean;
  oidcProviderLabel?: string;
  returnTo?: string;
};

export const SignInForm = ({
  className,
  initialEmail,
  isGoogleSSOEnabled,
  isOIDCSSOEnabled,
  oidcProviderLabel,
  returnTo,
}: SignInFormProps) => {
  useLocale();

  const navigate = useNavigate();

  const [isTwoFactorAuthenticationDialogOpen, setIsTwoFactorAuthenticationDialogOpen] =
    useState(false);

  const [twoFactorAuthenticationMethod, setTwoFactorAuthenticationMethod] = useState<
    'totp' | 'backup'
  >('totp');

  const [isPasskeyLoading, setIsPasskeyLoading] = useState(false);

  const redirectPath = useMemo(() => {
    // Handle SSR
    if (typeof window === 'undefined') {
      return LOGIN_REDIRECT_PATH;
    }

    let url = new URL(returnTo || LOGIN_REDIRECT_PATH, window.location.origin);

    // Don't allow different origins
    if (url.origin !== window.location.origin) {
      url = new URL(LOGIN_REDIRECT_PATH, window.location.origin);
    }

    return url.toString();
  }, [getLocale(), LOGIN_REDIRECT_PATH]);

  const { mutateAsync: createPasskeySigninOptions } =
    trpc.auth.createPasskeySigninOptions.useMutation();

  const form = useForm<TSignInFormSchema>({
    values: {
      email: initialEmail ?? '',
      password: '',
      totpCode: '',
      backupCode: '',
    },
    resolver: zodResolver(ZSignInFormSchema, { error: validationError }),
  });

  const isSubmitting = form.formState.isSubmitting;

  const onCloseTwoFactorAuthenticationDialog = () => {
    form.setValue('totpCode', '');
    form.setValue('backupCode', '');

    setIsTwoFactorAuthenticationDialogOpen(false);
  };

  const onToggleTwoFactorAuthenticationMethodClick = () => {
    const method = twoFactorAuthenticationMethod === 'totp' ? 'backup' : 'totp';

    if (method === 'totp') {
      form.setValue('backupCode', '');
    }

    if (method === 'backup') {
      form.setValue('totpCode', '');
    }

    setTwoFactorAuthenticationMethod(method);
  };

  const onSignInWithPasskey = async () => {
    if (!browserSupportsWebAuthn()) {
      toast.error(t('auth.notSupported'), {
        get description() {
          return t('auth.passkeysAreNotSupportedOnThisBrowser');
        },
        duration: 10000,
      });

      return;
    }

    try {
      setIsPasskeyLoading(true);

      const { options, sessionId } = await createPasskeySigninOptions();
      if (!sessionId) {
        return;
      }
      const credential = await startAuthentication({ optionsJSON: options });

      await authClient.passkey.signIn({
        credential: JSON.stringify(credential),
        csrfToken: sessionId,
        redirectPath,
      });
    } catch (err) {
      setIsPasskeyLoading(false);

      // Error from library.
      if (err instanceof Error && err.name === 'NotAllowedError') {
        return;
      }

      const error = AppError.parseError(err);

      const errorMessage = match(error.code)
        .with(AuthenticationErrorCode.NotSetup, () =>
          t('extra.thisPasskeyIsNotConfiguredForThisApplicationPleaseLogin'),
        )
        .with(AuthenticationErrorCode.SessionExpired, () =>
          t('extra.thisSessionHasExpiredPleaseTryAgain'),
        )
        .otherwise(() => handleFallbackErrorMessages(error.code));

      toast.error(t('auth.somethingWentWrong'), {
        description: errorMessage,
        duration: 10000,
      });
    }
  };

  const onFormSubmit = async ({ email, password, totpCode, backupCode }: TSignInFormSchema) => {
    try {
      await authClient.emailPassword.signIn({
        email,
        password,
        totpCode,
        backupCode,
        redirectPath,
      });
    } catch (err) {
      const error = AppError.parseError(err);

      if (error.code === 'TWO_FACTOR_MISSING_CREDENTIALS') {
        setIsTwoFactorAuthenticationDialogOpen(true);
        return;
      }

      if (error.code === AuthenticationErrorCode.UnverifiedEmail) {
        await navigate('/auth/unverified-account');

        toast.error(t('auth.unableToSignIn'), {
          get description() {
            return t('auth.thisAccountHasNotBeenVerifiedPleaseVerifyYour');
          },
        });

        return;
      }

      const errorMessage = match(error.code)
        .with(AuthenticationErrorCode.InvalidCredentials, () =>
          t('extra.theEmailOrPasswordProvidedIsIncorrect'),
        )
        .with(AuthenticationErrorCode.InvalidTwoFactorCode, () =>
          t('extra.theTwoFactorAuthenticationCodeProvidedIsIncorrect'),
        )
        .otherwise(() => handleFallbackErrorMessages(error.code));

      toast.error(t('auth.unableToSignIn'), {
        description: errorMessage,
      });
    }
  };

  const onSignInWithGoogleClick = async () => {
    try {
      console.log('onSignInWithGoogleClick');
      // await authClient.google.signIn({
      //   redirectPath,
      // });
    } catch (err) {
      toast.error(t('workspace.anUnknownErrorOccurred'), {
        get description() {
          return t('auth.weEncounteredAnUnknownErrorWhileAttemptingToSign');
        },
      });
    }
  };

  const onSignInWithOIDCClick = async () => {
    try {
      console.log('onSignInWithOIDCClick');
      // await authClient.oidc.signIn({
      //   redirectPath,
      // });
    } catch (err) {
      toast.error(t('workspace.anUnknownErrorOccurred'), {
        get description() {
          return t('auth.weEncounteredAnUnknownErrorWhileAttemptingToSign');
        },
      });
    }
  };

  useEffect(() => {
    const hash = window.location.hash.slice(1);

    const params = new URLSearchParams(hash);

    const email = params.get('email');

    if (email) {
      form.setValue('email', email);
    }
  }, [form]);

  return (
    <Form {...form}>
      <form
        className={cn('flex w-full flex-col gap-y-4', className)}
        onSubmit={form.handleSubmit(onFormSubmit)}
      >
        <fieldset
          className="flex w-full flex-col gap-y-4"
          disabled={isSubmitting || isPasskeyLoading}
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('reports.email')}</FormLabel>

                <FormControl>
                  <Input type="email" {...field} />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.password')}</FormLabel>

                <FormControl>
                  <PasswordInput {...field} />
                </FormControl>

                <FormMessage />

                <p className="mt-2 text-end">
                  <Link
                    className="text-muted-foreground text-sm duration-200 hover:opacity-70"
                    to="/auth/forgot-password"
                  >
                    {t('auth.forgotYourPassword')}{' '}
                  </Link>
                </p>
              </FormItem>
            )}
          />

          <Button
            className="dark:bg-primary dark:hover:opacity-90"
            loading={isSubmitting}
            size="lg"
            type="submit"
          >
            {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          {(isGoogleSSOEnabled || isOIDCSSOEnabled) && (
            <div className="relative flex items-center justify-center gap-x-4 py-2 text-xs uppercase">
              <div className="h-px flex-1 bg-border" />
              <span className="bg-transparent text-muted-foreground">
                {t('auth.orContinueWith')}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
          )}

          {isGoogleSSOEnabled && (
            <Button
              className="border bg-background text-muted-foreground"
              disabled={isSubmitting}
              onClick={onSignInWithGoogleClick}
              size="lg"
              type="button"
              variant="outline"
            >
              <GoogleIcon className="me-2 h-5 w-5" />
              {t('auth.google')}{' '}
            </Button>
          )}

          {isOIDCSSOEnabled && (
            <Button
              className="border bg-background text-muted-foreground"
              disabled={isSubmitting}
              onClick={onSignInWithOIDCClick}
              size="lg"
              type="button"
              variant="outline"
            >
              <OidcIcon className="me-2 h-5 w-5" />
              {oidcProviderLabel || 'OIDC'}
            </Button>
          )}

          <Button
            className="border bg-background text-muted-foreground"
            disabled={isSubmitting}
            loading={isPasskeyLoading}
            onClick={onSignInWithPasskey}
            size="lg"
            type="button"
            variant="outline"
          >
            {!isPasskeyLoading && <KeyRoundIcon className="me-1 -ml-1 h-5 w-5" />}
            {t('workspace.passkey')}{' '}
          </Button>
        </fieldset>
      </form>

      <Dialog
        onOpenChange={onCloseTwoFactorAuthenticationDialog}
        open={isTwoFactorAuthenticationDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('auth.twoFactorAuthentication')}</DialogTitle>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onFormSubmit)}>
            <fieldset disabled={isSubmitting}>
              {twoFactorAuthenticationMethod === 'totp' && (
                <FormField
                  control={form.control}
                  name="totpCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.token')}</FormLabel>
                      <FormControl>
                        <PinInput {...field} maxLength={6} value={field.value ?? ''}>
                          {Array(6)
                            .fill(null)
                            .map((_, i) => (
                              <PinInputGroup key={i}>
                                <PinInputSlot index={i} />
                              </PinInputGroup>
                            ))}
                        </PinInput>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {twoFactorAuthenticationMethod === 'backup' && (
                <FormField
                  control={form.control}
                  name="backupCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.backupCode')}</FormLabel>
                      <FormControl>
                        <Input type="text" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <DialogFooter className="mt-4">
                <Button
                  onClick={onToggleTwoFactorAuthenticationMethodClick}
                  type="button"
                  variant="secondary"
                >
                  {twoFactorAuthenticationMethod === 'totp'
                    ? t('auth.useBackupCode')
                    : t('auth.useAuthenticator')}
                </Button>

                <Button loading={isSubmitting} type="submit">
                  {isSubmitting ? t('auth.signingIn') : t('auth.signIn')}
                </Button>
              </DialogFooter>
            </fieldset>
          </form>
        </DialogContent>
      </Dialog>
    </Form>
  );
};
